import { useMemo } from 'react';
import { IconButton, useMediaQuery, useTheme } from '@mui/material';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { useTranslation } from 'react-i18next';

interface KpiBarDashboardLinkProps {
  dashboardId: string;
}

// Generates a link to the appropriate dashboard based on the dashboardId.
const KpiBarDashboardLink = ({ dashboardId }: KpiBarDashboardLinkProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { activeSite } = useCosmos();

  const { dashboardHref, dashboardLabel } = useMemo(() => {
    // Extract the appId from dashboardId (e.g., "editorial" from "editorial.search")
    const [appId] = dashboardId.split('.');

    // Map appId to dashboard paths and labels
    const kpiToDashboardMap: Record<string, { path: string; label: string }> = {
      editorial: {
        path: 'editorial/dashboard/',
        label: t('dashboards.titles.editorial'),
      },
      // (users).
      subscription: {
        path: 'user/dashboard',
        label: t('dashboards.titles.subscription'),
      },
      emailreach: {
        path: 'emailreach/dashboard',
        label: t('dashboards.titles.emailreach'),
      },
      journeys: {
        path: 'journeys/dashboard',
        label: t('dashboards.titles.journeys'),
      },
    };

    const defaultConfig = {
      path: `${appId}/dashboard`,
      label: t('dashboards.titles.default'),
    };

    const config = kpiToDashboardMap[appId] || defaultConfig;

    return {
      dashboardHref: `/${activeSite}/${config.path}`,
      dashboardLabel: config.label,
    };
  }, [dashboardId, activeSite, t]);

  return (
    <IconButton
      aria-label={dashboardLabel}
      href={dashboardHref}
      sx={{
        p: isMobileOrTablet ? 0.5 : 'auto',
      }}
    >
      <QueryStatsRoundedIcon fontSize={isMobileOrTablet ? 'small' : 'medium'} />
    </IconButton>
  );
};

export default KpiBarDashboardLink;
